import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  Injector,
  effect,
  inject,
  signal,
  viewChild,
  viewChildren
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CommandPaletteNavigationSearchService } from '@mogenius/core/services/command-palette-navigation-search.service';
import { CommandPaletteResourceSearchService } from '@mogenius/core/services/command-palette-resource-search.service';
import {
  Command,
  CommandPaletteService,
  NavigationResult,
  ResourceResult
} from '@mogenius/core/services/command-palette.service';
import {
  ExecutableQuickAction,
  QuickActionContext,
  getActionsForNavigation,
  getActionsForResource,
  findActionByShortcut
} from '@mogenius/core/services/command-palette-quick-actions.config';
import { NavigationService } from '@mogenius/shared/services/navigation/navigation.service';
import { ClusterService } from '@mogenius/cluster/services/cluster-service/cluster.service';
import { SpinnerComponent } from '@mogenius/shared/components/spinner/spinner.component';

/** One step of the scope shown in the header. Informative only — nothing is clickable. */
export interface CommandPaletteScopeSegment {
  readonly kind: 'organization' | 'cluster' | 'workspace';
  readonly label: string;
}

const SCOPE_ICONS: Record<CommandPaletteScopeSegment['kind'], string> = {
  organization: 'pi pi-building',
  cluster: 'pi pi-sitemap',
  workspace: 'pi pi-th-large'
};

@Component({
  selector: 'mo-command-palette',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, SpinnerComponent],
  templateUrl: './command-palette.component.html',
  styleUrl: './command-palette.component.scss'
})
export class CommandPaletteComponent {
  protected readonly commandPalette = inject(CommandPaletteService);
  private readonly _resourceSearch = inject(CommandPaletteResourceSearchService);
  private readonly _navigationSearch = inject(CommandPaletteNavigationSearchService);
  private readonly _injector = inject(Injector);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _router = inject(Router);
  private readonly _navigationService = inject(NavigationService);
  private readonly _clusterService = inject(ClusterService);

  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  private readonly resultsInner = viewChild<ElementRef<HTMLDivElement>>('resultsInner');
  private readonly cmdItems = viewChildren<ElementRef<HTMLButtonElement>>('cmdItem');

  protected readonly selectedIndex = signal<number>(0);
  protected readonly pointerMoved = signal(false);

  // NavigationService already fronts OrganizationService, ClusterService and
  // WorkspaceService, so the scope reads from one place and needs no new wiring.
  private readonly _selectedOrganization = toSignal(this._navigationService.selectedOrganization$);
  private readonly _selectedCluster = toSignal(this._navigationService.selectedCluster$);
  private readonly _selectedWorkspace = toSignal(this._navigationService.selectedWorkspace$);

  /**
   * Organization first, then whatever else is actually selected. Nothing is
   * rendered for a step that has no value, so an organization-only context shows
   * a single chip rather than empty placeholders.
   */
  protected readonly scopeSegments = computed<CommandPaletteScopeSegment[]>(() => {
    const segments: CommandPaletteScopeSegment[] = [];

    const organization = this._selectedOrganization()?.name;
    if (organization) segments.push({ kind: 'organization', label: organization });

    const cluster = this._selectedCluster()?.displayName;
    if (cluster) segments.push({ kind: 'cluster', label: cluster });

    const workspace = this._selectedWorkspace()?.name;
    if (workspace) segments.push({ kind: 'workspace', label: workspace });

    return segments;
  });

  /** The footer hints belong to the zero-query state only; typing replaces them with results. */
  protected readonly showZeroQueryHints = computed(() => this.commandPalette.query().trim().length === 0);

  private _resizeObserver: ResizeObserver | null = null;

  // All navigable items: commands + navigation + resources (for keyboard nav)
  private readonly flatCommands = computed(() => this.commandPalette.groupedCommands().flatMap((g) => g.commands));

  private readonly totalItems = computed(
    () =>
      this.flatCommands().length +
      this.commandPalette.navigationResults().length +
      this.commandPalette.resourceResults().length
  );

  @HostListener('document:keydown.escape')
  onEscapeGlobal(): void {
    if (this.commandPalette.isOpen()) {
      this.commandPalette.close();
    }
  }

  constructor() {
    effect(() => {
      if (this.commandPalette.isOpen()) {
        afterNextRender(
          () => {
            this.searchInput()?.nativeElement.focus();
            this.observeResultsHeight();
          },
          { injector: this._injector }
        );
      } else {
        this.disconnectResizeObserver();
      }
    });

    effect(() => {
      const idx = this.selectedIndex();
      if (this.commandPalette.isOpen() && !this.pointerMoved()) {
        afterNextRender(() => this.scrollSelectedIntoView(idx), { injector: this._injector });
      }
    });

    this._destroyRef.onDestroy(() => this.disconnectResizeObserver());
  }

  private scrollSelectedIntoView(index: number): void {
    const items = this.cmdItems();
    items[index]?.nativeElement.scrollIntoView({ block: 'nearest', behavior: 'instant' });
  }

  private observeResultsHeight(): void {
    this.disconnectResizeObserver();

    const innerEl = this.resultsInner()?.nativeElement;
    if (!innerEl) return;

    const resultsEl = innerEl.parentElement;
    if (!resultsEl) return;

    this._resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.contentRect.height + 16;
        resultsEl.style.height = `${Math.min(height, 340)}px`;
      }
    });

    this._resizeObserver.observe(innerEl);
  }

  private disconnectResizeObserver(): void {
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
  }

  protected onQueryChange(value: string): void {
    this.commandPalette.setQuery(value);
    this._resourceSearch.search(value);
    this._navigationSearch.search(value);
    this.selectedIndex.set(0);
  }

  protected onKeydown(event: KeyboardEvent): void {
    this.pointerMoved.set(false);
    const total = this.totalItems();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex.update((i) => (i + 1) % Math.max(total, 1));
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex.update((i) => (i - 1 + Math.max(total, 1)) % Math.max(total, 1));
        break;

      case 'Enter':
        event.preventDefault();
        this.executeSelectedItem();
        break;

      case 'Escape':
        event.preventDefault();
        this.commandPalette.close();
        break;

      default:
        if (this.handleQuickActionShortcut(event)) {
          event.preventDefault();
        }
        break;
    }
  }

  private executeSelectedItem(): void {
    const idx = this.selectedIndex();
    const cmds = this.flatCommands();
    const navs = this.commandPalette.navigationResults();

    if (idx < cmds.length) {
      this.commandPalette.execute(cmds[idx]);
    } else if (idx < cmds.length + navs.length) {
      const navIdx = idx - cmds.length;
      this.executeNavigation(navs[navIdx]);
    } else {
      const resourceIdx = idx - cmds.length - navs.length;
      const resources = this.commandPalette.resourceResults();
      if (resources[resourceIdx]) {
        this.commandPalette.executeResource(resources[resourceIdx]);
      }
    }
  }

  protected onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('mo-cmd-backdrop')) {
      this.commandPalette.close();
    }
  }

  protected executeCommand(cmd: Command): void {
    this.commandPalette.execute(cmd);
  }

  protected executeResource(result: ResourceResult): void {
    this.commandPalette.executeResource(result);
  }

  protected executeNavigation(nav: NavigationResult): void {
    this.commandPalette.close();
    nav.execute();
  }

  protected getGlobalIndex(groupIndex: number, cmdIndex: number): number {
    const groups = this.commandPalette.groupedCommands();
    let offset = 0;
    for (let i = 0; i < groupIndex; i++) {
      offset += groups[i].commands.length;
    }
    return offset + cmdIndex;
  }

  protected getNavigationGlobalIndex(navIndex: number): number {
    return this.flatCommands().length + navIndex;
  }

  protected getResourceGlobalIndex(resourceIndex: number): number {
    return this.flatCommands().length + this.commandPalette.navigationResults().length + resourceIndex;
  }

  protected onPointerMove(): void {
    if (!this.pointerMoved()) {
      this.pointerMoved.set(true);
    }
  }

  protected onItemHover(index: number): void {
    if (this.pointerMoved()) {
      this.selectedIndex.set(index);
    }
  }

  protected scopeIcon(kind: CommandPaletteScopeSegment['kind']): string {
    return SCOPE_ICONS[kind];
  }

  /**
   * NavigationResult.type and ResourceScope.kind carry the distinction; this only turns
   * it into a label. The colour that goes with it (pink for clusters, blue for
   * workspaces) hangs off the [data-type] attribute in the scss.
   *
   * TODO(design): the wording of the two labels is still open.
   */
  protected typeLabel(type: 'cluster' | 'workspace'): string {
    return type === 'cluster' ? 'Cluster' : 'Workspace';
  }

  protected getResourceActions(resource: ResourceResult): ExecutableQuickAction[] {
    return getActionsForResource(resource);
  }

  protected getNavigationActions(nav: NavigationResult): ExecutableQuickAction[] {
    return getActionsForNavigation(nav.type);
  }

  protected executeQuickAction(
    action: ExecutableQuickAction,
    item: ResourceResult | NavigationResult,
    event: MouseEvent
  ): void {
    event.stopPropagation();
    this.commandPalette.close();

    const ctx: QuickActionContext = {
      router: this._router,
      orgId: this._navigationService.selectedOrganization$.value?.id ?? '',
      clusterId: this._clusterService.selectedCluster$.value?.id,
      workspaceName: this._navigationService.selectedWorkspace$.value?.name
    };

    action.execute(item, ctx);
  }

  protected handleQuickActionShortcut(event: KeyboardEvent): boolean {
    const idx = this.selectedIndex();
    const cmds = this.flatCommands();
    const navs = this.commandPalette.navigationResults();
    const resources = this.commandPalette.resourceResults();

    let actions: ExecutableQuickAction[] = [];
    let item: ResourceResult | NavigationResult | null = null;

    if (idx >= cmds.length && idx < cmds.length + navs.length) {
      const navIdx = idx - cmds.length;
      item = navs[navIdx];
      actions = getActionsForNavigation(navs[navIdx].type);
    } else if (idx >= cmds.length + navs.length) {
      const resourceIdx = idx - cmds.length - navs.length;
      if (resources[resourceIdx]) {
        item = resources[resourceIdx];
        actions = getActionsForResource(resources[resourceIdx]);
      }
    }

    // if (item && actions.length > 0) {
    //   const action = findActionByShortcut(actions, event.key);
    //   if (action) {
    //     this.commandPalette.close();
    //     const navItem = item as NavigationResult;
    //     const ctx: QuickActionContext = {
    //       router: this._router,
    //       orgId: this._navigationService.selectedOrganization$.value?.id ?? '',
    //       clusterId: navItem.clusterId ?? this._clusterService.selectedCluster$.value?.id,
    //       workspaceName: this._navigationService.selectedWorkspace$.value?.name
    //     };
    //     action.execute(item, ctx);
    //     return true;
    //   }
    // }

    return false;
  }
}
